import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiValidationError } from '../models/BaseModel';

/** Response header stamped by the API's CorrelationIdMiddleware. */
export const CORRELATION_HEADER = 'x-correlation-id';

/** What to show the user: a SweetAlert title plus pre-escaped HTML body. */
export interface DisplayableError {
  title: string;
  html: string;
}

/** The failure envelope the API returns - always with HTTP 200 for handled failures. */
export interface ApiFailureBody {
  result?: boolean;
  messages?: Array<string>;
  errors?: Array<ApiValidationError>;
  traceId?: string;
}

/**
 * Turns an API failure into something a user can act on.
 *
 * Exists because the reason for a failure arrives in three different places depending on the kind
 * of failure - `errors[]` for field-level validation, `messages[]` for business rules, and nothing
 * at all for an unexpected server error (where only the trace id is useful). Previously each
 * caller picked one of those and showed a constant string for the rest.
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorService {

  /**
   * Summary lines the API sends alongside the real detail. They tell the user nothing, so they are
   * dropped whenever something specific is available. Compared lowercased.
   *
   * The first is still produced by API builds older than the trace-id change - keep it here so the
   * portal degrades gracefully when the two are deployed out of step.
   */
  private readonly genericMessages = [
    'an error occurred while processing your request.',
    'an error occurred while processing your request',
    'incomplete information. please check and resubmit',
    'your request could not be completed. nothing was saved. please try again.',
  ];

  /** Failure carried in a 200 response body (`result: false`). */
  fromBody(body: ApiFailureBody | null | undefined): DisplayableError {
    const fieldLines = this.fieldLines(body?.errors);
    const messageLines = this.specificMessages(body?.messages);

    if (fieldLines.length) {
      return {
        title: 'Please check your entries',
        html: this.compose(fieldLines, body?.traceId),
      };
    }

    if (messageLines.length) {
      return { title: 'Error', html: this.compose(messageLines, body?.traceId) };
    }

    return this.unexpected(body?.traceId);
  }

  /** Failure that never produced a usable body - network drop, auth rejection, unhandled 500. */
  fromHttpError(error: HttpErrorResponse): DisplayableError {
    const traceId = this.traceIdOf(error);

    // status 0 means the request never reached the API (offline, DNS, CORS, cancelled).
    if (error.status === 0) {
      return {
        title: 'No connection',
        html: this.compose(
          ['Could not reach the server. Check your internet connection and try again.'],
          undefined,
        ),
      };
    }

    if (error.status === 401 || error.status === 403) {
      return {
        title: 'Session expired',
        html: this.compose(['Your session is no longer valid. Please sign in again.'], undefined),
      };
    }

    if (error.status === 413) {
      return {
        title: 'Attachment too large',
        html: this.compose(
          ['The files you attached exceed the maximum upload size. Attach smaller files and try again.'],
          traceId,
        ),
      };
    }

    // A 400/500 can still carry the standard envelope - prefer it over a generic string.
    const body = error.error as ApiFailureBody | undefined;
    const fieldLines = this.fieldLines(body?.errors);
    const messageLines = this.specificMessages(body?.messages);

    if (fieldLines.length) {
      return { title: 'Please check your entries', html: this.compose(fieldLines, traceId) };
    }

    if (messageLines.length) {
      return { title: 'Error', html: this.compose(messageLines, traceId) };
    }

    return this.unexpected(traceId);
  }

  /** True when a 200 response body represents a handled failure. */
  isFailureBody(body: any): boolean {
    return !!body && body.result === false;
  }

  /**
   * Trace id for a failed request: the body's own value when the API built a response, otherwise
   * the header, which survives even when the request died before reaching a controller.
   */
  traceIdOf(error: HttpErrorResponse): string | undefined {
    const fromBody = (error.error as ApiFailureBody | undefined)?.traceId;
    return fromBody || error.headers?.get(CORRELATION_HEADER) || undefined;
  }

  private unexpected(traceId?: string): DisplayableError {
    return {
      title: 'Something went wrong',
      html: this.compose(
        ['Your request could not be completed. Nothing was saved. Please try again.'],
        traceId,
      ),
    };
  }

  private fieldLines(errors?: Array<ApiValidationError>): string[] {
    if (!errors?.length) return [];

    return errors
      .filter(e => !!e?.message)
      .map(e => (e.field ? `${e.field}: ${e.message}` : e.message));
  }

  private specificMessages(messages?: Array<string>): string[] {
    if (!messages?.length) return [];

    const specific = messages
      .filter(m => !!m && m.trim().length > 0)
      .filter(m => !this.genericMessages.includes(m.trim().toLowerCase()));

    // Every line was a known summary: the caller falls back to the unexpected-error wording, which
    // at least carries the reference id.
    return specific;
  }

  private compose(lines: string[], traceId?: string): string {
    const body = lines.map(line => `<p style="margin:0 0 .5rem">${this.escape(line)}</p>`).join('');

    if (!traceId) return body;

    // Shown in full and selectable on purpose: a truncated id cannot be looked up in
    // Application Insights (operation_Id == "<id>").
    return `${body}<p style="margin:1rem 0 0;font-size:.85em;color:#6c757d">`
      + `If the problem continues, contact support and quote this reference:<br>`
      + `<code style="user-select:all;word-break:break-all">${this.escape(traceId)}</code></p>`;
  }

  /** Server-supplied text goes into innerHTML, so it must be escaped. */
  private escape(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
